import { BACKEND_URL } from '../config';

const SESSION_CHECK_INTERVAL = 60 * 1000; // Check every 60 seconds
const SESSION_WARNING_TIME = 5 * 60 * 1000; // Warn 5 minutes before expiry
const SESSION_DURATION = 24 * 60 * 60 * 1000; // 24 hours

let sessionCheckInterval = null;
let lastActivityTime = Date.now();

// Track user activity
const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];

function updateActivity() {
  lastActivityTime = Date.now();
}

// Set up activity listeners
export function initActivityTracking() {
  activityEvents.forEach(event => {
    document.addEventListener(event, updateActivity, true);
  });
}

// Clean up listeners
export function cleanupActivityTracking() {
  activityEvents.forEach(event => {
    document.removeEventListener(event, updateActivity, true);
  });
}

// Check if session is still valid
export async function validateSession() {
  try {
    const loginDate = localStorage.getItem('pos_login_date');
    if (!loginDate) return false;

    const loginTime = new Date(loginDate).getTime();
    const now = Date.now();
    const timeSinceLogin = now - loginTime;

    // If more than 24 hours, session expired
    if (timeSinceLogin > SESSION_DURATION) {
      return false;
    }

    return true;
  } catch (error) {
    console.error('[Session] Error validating session:', error);
    return false;
  }
}

// Handle SSO authentication from GHL
export async function handleSSOAuth(encryptedData, setChecking) {
  try {
    console.log('[SSO] Starting SSO authentication with encrypted data');
    
    const response = await fetch(`${BACKEND_URL}/api/auth/decrypt-sso`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ encryptedData }),
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`SSO decrypt failed: ${response.statusText}`);
    }

    const data = await response.json();
    
    if (data.ok) {
      // Store session_id in localStorage for iframe context (cookies don't work in iframes)
      if (data.session_id) {
        console.log('[SSO] 💾 Storing session_id in localStorage:', data.session_id.substring(0, 20) + '...');
        localStorage.setItem('pos_session_id', data.session_id);
        console.log('[SSO] ✅ Verification - localStorage now has:', localStorage.getItem('pos_session_id') ? 'YES' : 'NO');
      } else {
        console.error('[SSO] ❌ No session_id in response! Response:', data);
      }
      
      // Get current session from backend
      const currentSession = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: 'include',
        headers: {
          'Authorization': data.session_id ? `Bearer ${data.session_id}` : ''
        }
      });

      if (currentSession.ok) {
        const sessionData = await currentSession.json();
        console.log('[SSO] ✅ SSO authentication successful');
        
        const today = new Date().toISOString().slice(0,10);
        localStorage.setItem('pos_login_date', today);
        
        return { success: true, data: sessionData };
      }
    }
    
    return { success: false };
  } catch (error) {
    console.error('[SSO] ❌ SSO authentication failed:', error);
    return { success: false, error };
  } finally {
    if (setChecking) setChecking(false);
  }
}

// Start session monitoring
export function startSessionMonitoring(onSessionExpired) {
  if (sessionCheckInterval) return; // Already running

  sessionCheckInterval = setInterval(async () => {
    const isValid = await validateSession();
    if (!isValid && onSessionExpired) {
      stopSessionMonitoring();
      onSessionExpired();
    }
  }, SESSION_CHECK_INTERVAL);
}

// Stop session monitoring
export function stopSessionMonitoring() {
  if (sessionCheckInterval) {
    clearInterval(sessionCheckInterval);
    sessionCheckInterval = null;
  }
}

// Initialize session validation (called from App.js)
export function initializeSessionValidation() {
  console.log('[Session] Initializing session validation');
  initActivityTracking();
  
  // Start monitoring session
  startSessionMonitoring(() => {
    console.log('[Session] Session expired, redirecting to login');
    window.location.href = '/login';
  });
}
